import { DownloadOutlined, FilterOutlined, PlusOutlined, RedoOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Popover, Space, Table, Tooltip, Tree } from 'antd';
import type { ColumnType, TableProps } from 'antd/es/table';
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import type { DataNode } from 'antd/es/tree';
import { Rule } from 'async-validator';
import { get, isArray, min } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Format } from 'src/components/format';
import { MAX_TABLE_RECORDS } from 'src/config';
import { Omits } from 'src/lib/lang/tsHelpers';

import { EditableCell } from './ad-editable-cell';

export declare type ExtraTool = {
  icon?: React.ReactElement;
  tooltip?: string;
  handler?: () => void;
  render?: () => JSX.Element;
};

export type AdTableOnChangeOpts = {
  _limit?: number;
  _offset?: number;
  _sort?: string;
};

export type AdColumnsType<RecordType> = AdColumnType<RecordType>[];
export interface AdColumnType<RecordType> extends ColumnType<RecordType> {
  defaultHidden?: boolean;
  filterDisabled?: boolean;
  compute?: (val: any, record: RecordType) => any;
  _width?: number;
  rules?: Rule;
  disabled?: boolean;
}

interface AdTableProps<RecordType> extends Omits<TableProps<RecordType>, ['columns', 'onChange', 'title']> {
  columns?: AdColumnsType<RecordType>;
  columnFilterTooltip?: string; // 列筛选按钮tip
  exportTooltip?: string; // 导出按钮tip
  extraTools?: React.ReactElement; // 扩展栏，在工具栏左侧，可自定义
  extraToolsAppend?: React.ReactElement; // 扩展栏，在工具栏右侧，可自定义
  refreshTooltip?: string; // 刷新按钮tip
  addNewTooltip?: string; // 新建按钮tip
  uploadTooltip?: string; // 上传按钮tip
  showColumnsFilter?: boolean; // 显示列筛选按钮
  title?: string | React.ReactNode; // 表格名称
  tooltip?: React.ReactNode; // 表格顶部组件
  wrapperStyle?: React.CSSProperties;
  onChange?: (opt: AdTableOnChangeOpts) => void; // 分页、筛选、排序变化时触发
  onRefresh?: () => void; // 刷新相应
  onExport?: () => void; // 导出响应
  onAddNew?: () => void; // 新建响应
  onUpload?: () => void; // 上传响应
}

let locale = {
  emptyText: '--',
};

const ExtraButtonStyle = {
  color: '#999',
  padding: 0,
  lineHeight: 0,
};

const ExtraContainer = styled.div`
  padding: 0px 12px;
`;

const isDataCol = (col) => !!(col.dataIndex || col.key);

/**
 * title: 表格标题
 * dataIndex: 列数据在数据项中对应的路径，支持通过数组查询嵌套路径
 * key: 如果 dataIndex 不是一个唯一的 string，需要为这一列指定一个 key
 */
function columnsToTreeData(columns: AdColumnsType<any> = []): DataNode[] {
  return columns
    .filter(isDataCol)
    .filter((col) => !col.disabled)
    .map((col: AdColumnType<any>) => ({
      title: col.title as string,
      key: getColKey(col) as string,
      disabled: col.filterDisabled,
    }));
}

const getColKey = (col: AdColumnType<any>) => (col.key || col.dataIndex) as string;

const components = {
  body: {
    cell: EditableCell,
  },
};

export function AdTable<RecordType extends object = any>({
  columnFilterTooltip = '列筛选',
  columns = [],
  exportTooltip = '导出',
  extraTools,
  extraToolsAppend,
  pagination,
  refreshTooltip = '刷新',
  addNewTooltip = '新建',
  uploadTooltip = '上传',
  showColumnsFilter = true,
  title,
  tooltip,
  wrapperStyle,
  onExport,
  onRefresh,
  onChange,
  onAddNew,
  onUpload,
  rowKey = 'id',
  ...rest
}: AdTableProps<RecordType>) {
  const defaultShowKeys = useMemo(
    () =>
      columns
        .filter((col: AdColumnType<RecordType>) => isDataCol(col) && !col.defaultHidden && !col.disabled)
        .map((col: AdColumnType<RecordType>) => getColKey(col)),
    [columns]
  );
  const [showKeys, setShowKeys] = React.useState<string[]>(defaultShowKeys);
  const treeData = useMemo(() => columnsToTreeData(columns), [columns]);
  const showColumns = useMemo(
    () =>
      columns
        .filter((col: AdColumnType<RecordType>) => !isDataCol(col) || showKeys.includes(getColKey(col)))
        .map((c) => {
          const { compute, render } = c;
          if (!render && compute) {
            return {
              ...c,
              render: (val: any, record: RecordType) => {
                const formatter = (v: any) => compute(v, record);
                return <Format value={val} formatter={formatter} />;
              },
            };
          }
          if (!render && c.dataIndex) {
            return { ...c, render: (val: any) => <Format value={val} /> };
          }
          return c;
        }),
    [columns, showKeys]
  );

  const handleChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<RecordType> | SorterResult<RecordType>[]
    ) => {
      if (onChange) {
        const values: AdTableOnChangeOpts = {};
        const { current = 1, pageSize = 10 } = pagination;
        values._limit = pageSize;
        values._offset = (current - 1) * pageSize;

        // 这个地方排序只处理了单列排序
        // TODO: 多列排序
        const { field, order } = sorter as SorterResult<RecordType>;
        if (order) values._sort = `${order === 'ascend' ? '' : '-'}${isArray(field) ? field.join('.') : field}`;

        onChange({ ...values, ...filters });
      }
    },
    [onChange]
  );

  let total = get(pagination, 'total') || rest.dataSource?.length || 0;
  const realTotal = total;
  total = min([realTotal, MAX_TABLE_RECORDS]);

  const filterTree = (
    <div style={{ marginLeft: -12 }}>
      <Tree
        checkable
        checkedKeys={showKeys}
        onCheck={(checkedKeys) => {
          setShowKeys(checkedKeys as string[]);
        }}
        selectable={false}
        treeData={treeData}
      />
    </div>
  );

  return (
    <Container
      title={title}
      style={wrapperStyle}
      bodyStyle={{
        padding: '12px 24px',
      }}
      extra={
        <Space>
          <ExtraContainer>{extraTools}</ExtraContainer>

          {onAddNew && (
            <Tooltip title={addNewTooltip}>
              <Button type="link" size="large" style={ExtraButtonStyle} onClick={onAddNew}>
                <PlusOutlined />
              </Button>
            </Tooltip>
          )}

          {onRefresh && (
            <Tooltip title={refreshTooltip}>
              <Button type="link" size="large" style={ExtraButtonStyle} onClick={onRefresh}>
                <RedoOutlined />
              </Button>
            </Tooltip>
          )}

          {showColumnsFilter && (
            <Popover placement="bottomRight" content={filterTree} arrow={false} title={columnFilterTooltip}>
              <Button type="link" size="large" style={ExtraButtonStyle}>
                <FilterOutlined />
              </Button>
            </Popover>
          )}

          {onUpload && (
            <Tooltip title={uploadTooltip}>
              <Button type="link" size="large" style={ExtraButtonStyle} onClick={onUpload}>
                <UploadOutlined />
              </Button>
            </Tooltip>
          )}

          {onExport && (
            <Tooltip title={exportTooltip}>
              <Button type="link" size="large" style={ExtraButtonStyle} onClick={onExport}>
                <DownloadOutlined />
              </Button>
            </Tooltip>
          )}

          {extraToolsAppend}
        </Space>
      }
    >
      {tooltip}
      <Table
        components={components}
        locale={locale}
        columns={showColumns}
        onChange={handleChange}
        rowKey={rowKey}
        pagination={
          pagination && {
            ...pagination,
            total,
            showSizeChanger: true,
            showTotal: () => `共 ${realTotal} 条`,
          }
        }
        {...rest}
      />
    </Container>
  );
}

const Container = styled(Card)`
  border: none !important;
  .ant-card-head {
    border: 0 !important;
    .ant-card-extra {
      padding: 8px 0;
    }
  }
`;
